import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import SubHeaders from '@/components/Headers/SubHeaders'
import { AlertError, Button, Loading } from 'vnetwork-ui'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { paths } from '@/utils/path'
import {
    createDataAccessAssignment,
    getDataAccessAssignmentById,
    updateDataAccessAssignment,
} from '../../services'
import type { DataAccessFormValues } from '../../types'
import { dataAccessFormSchema, formToPayload, prefillFromBE } from './schema'
import ResourcePicker from './ResourcePicker'
import SectionWho from './SectionWho'
import SectionScope from './SectionScope'
import SectionHideColumns from './SectionHideColumns'

const DEFAULT_VALUES: DataAccessFormValues = {
    name: '',
    description: '',
    keycloak_name: '',
    who_mode: 'employees',
    assignees: [],
    who_departments: [],
    ignores: [],
    scope_mode: 'self_plus_teammates',
    teammates: [],
    scope_departments: [],
    reject_columns: [],
}

const CreateEditDataAccess = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const { t } = useTranslation('settings')
    const isEdit = Boolean(id)

    const { control, handleSubmit, reset, watch, setValue, register } =
        useForm<DataAccessFormValues>({
            resolver: yupResolver(dataAccessFormSchema) as any,
            defaultValues: DEFAULT_VALUES,
        })

    // Fetch for edit mode
    const {
        data: existingData,
        isLoading: isLoadingExisting,
        isError: isErrorExisting,
        error: errorExisting,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.DATA_ACCESS_ASSIGNMENTS, id],
            queryFn: () => getDataAccessAssignmentById(id!),
            enabled: isEdit,
        },
        { showLoading: false, showError: false }
    )

    // Prefill on edit
    useEffect(() => {
        if (existingData?.result && isEdit) {
            reset(prefillFromBE(existingData.result))
        }
    }, [existingData, isEdit, reset])

    const createMutation = useMutationWithHandler(
        { mutationFn: createDataAccessAssignment },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            successMessage: t('data-access-success-created'),
            errorMessage: t('data-access-error-create'),
            onSuccess: () => navigate(paths.dataAccess),
        }
    )

    const updateMutation = useMutationWithHandler(
        {
            mutationFn: ({
                id: ruleId,
                payload,
            }: {
                id: string
                payload: any
            }) => updateDataAccessAssignment(ruleId, payload),
        },
        {
            reactQueryKeys: QUERY_KEYS.DATA_ACCESS_ASSIGNMENTS,
            successMessage: t('data-access-success-updated'),
            errorMessage: t('data-access-error-update'),
            onSuccess: () => navigate(paths.dataAccess),
        }
    )

    const isMutating = createMutation.isPending || updateMutation.isPending

    const onSubmit = (values: DataAccessFormValues) => {
        const payload = formToPayload(values)
        if (isEdit && id) {
            updateMutation.mutate({ id, payload })
        } else {
            createMutation.mutate(payload)
        }
    }

    if (isLoadingExisting) {
        return <Loading.CircularProgress width={20} height={20} />
    }

    if (isErrorExisting) {
        return <AlertError error={errorExisting} />
    }

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    { path: paths.dataAccess, title: t('data-access') },
                    {
                        path: isEdit
                            ? paths.dataAccessEdit
                            : paths.dataAccessCreate,
                        title: isEdit
                            ? t('data-access-edit')
                            : t('data-access-create'),
                    },
                ]}
            />

            <div className="mt-2 max-w-2xl">
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {/* Header card: name + description + resource picker */}
                    <div className="bg-white rounded-lg p-6 mb-4 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('data-access-name')}
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                                {...register('name')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder={t('data-access-name-placeholder')}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('data-access-description')}
                            </label>
                            <textarea
                                {...register('description')}
                                rows={2}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder={t(
                                    'data-access-description-placeholder'
                                )}
                            />
                        </div>
                        <ResourcePicker control={control} />
                    </div>

                    <SectionWho
                        control={control}
                        watch={watch}
                        setValue={setValue}
                    />
                    <SectionScope control={control} watch={watch} />
                    <SectionHideColumns control={control} watch={watch} />

                    <div className="flex justify-end gap-3 pb-8">
                        <Button
                            variant="outlined"
                            onClick={() => navigate(paths.dataAccess)}
                            disabled={isMutating}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isMutating}
                            disabled={isMutating}
                        >
                            {isEdit
                                ? t('update-resource')
                                : t('create-resource')}
                        </Button>
                    </div>
                </form>
            </div>
        </>
    )
}

export default CreateEditDataAccess
